import { useEffect, useRef, useState } from "react";
import { Map as MapLibreMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/maplibreWorker";
import { AlertTriangle, Camera, CalendarDays, CheckCircle2, Compass, Info, KeyRound, Loader2, MapPinned, Map as MapIcon, Navigation, Newspaper, Plus, Route as RouteIcon, Save, Sparkles, Trash2, Trophy, X } from "lucide-react";
import { toast } from "sonner";
import CompanionPresence from "@/components/admin/CompanionPresence";
import RouteBuilderModal from "@/components/admin/RouteBuilderModal";
import { MAP_STYLE_URL, MAP_STYLE_CONFIG_ERROR, MAP_STYLE_LOAD_ERROR_MESSAGE } from "@/lib/mapStyle";

// --- Routes (per day) ----------------------------------------------------------

function emptyRouteForm() {
  return { title: "", distance_km: "", description: "", map_image_url: "", map_pdf_url: "", sort_order: 0, route_group_id: "" };
}

// Sprint 01: <select> of active route groups for this event, used by both the route
// edit form and the "add route" form. "" always means "no group" (legacy behaviour).
function RouteGroupSelect({ value, onChange, routeGroups, testId }) {
  return (
    <select value={value} onChange={onChange} data-testid={testId} className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan">
      <option value="">Geen routekeuze</option>
      {routeGroups.map((g) => (
        <option key={g.id} value={g.id}>{g.name}{g.distance_km != null ? ` (${g.distance_km} km)` : ""}</option>
      ))}
    </select>
  );
}

// Sprint 06 (GPX upload) + Sprint 09A (route builder): the route editor's single
// "navigation" control — offers both ways to give a route GPS navigation data (upload
// a GPX file, or draw+route one directly on a map), and previews/manages whichever one
// is currently saved. Either origin produces the exact same canonical
// event_route_navigation shape, so StapKompas (Sprint 07/08/09) never needs to care
// which one was used.
function RouteGpxField({ route, authAxios, onChanged, eventLocation }) {
  const inputRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [mapError, setMapError] = useState(MAP_STYLE_CONFIG_ERROR);
  const summary = Array.isArray(route.event_route_navigation)
    ? route.event_route_navigation[0]
    : route.event_route_navigation;
  const [nav, setNav] = useState(summary || null);
  const sourceType = nav?.source_type || (nav ? "gpx" : null); // legacy rows: treat as gpx

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    if (sourceType === "builder" && !window.confirm("Deze route is gemaakt op de kaart. Wil je deze vervangen door een GPX-bestand?")) {
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/routes/${route.id}/navigation`, fd);
      toast.success(`GPX geüpload — ${(data.distance_meters / 1000).toFixed(2)} km`);
      setNav(data);
      setShowPreview(true);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeNav = async () => {
    if (!window.confirm("Navigatiedata voor deze route verwijderen?")) return;
    try {
      await authAxios.delete(`/admin/routes/${route.id}/navigation`);
      toast.success("Navigatiedata verwijderd");
      setNav(null);
      setShowPreview(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const ensureFullNav = async () => {
    if (nav?.geojson) return nav;
    setLoadingPreview(true);
    try {
      const { data } = await authAxios.get(`/admin/routes/${route.id}/navigation`);
      setNav(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  const togglePreview = async () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    const full = await ensureFullNav();
    if (full) setShowPreview(true);
  };

  const openBuilder = async () => {
    if (nav) {
      const full = await ensureFullNav();
      if (!full) return;
    }
    setShowBuilder(true);
  };

  useEffect(() => {
    if (!showPreview || !nav?.geojson || !mapContainerRef.current) return undefined;
    if (MAP_STYLE_CONFIG_ERROR) {
      // Kept as a guard: never attempt to load a map style known in advance to be
      // unusable (avoids an unexplained blank preview).
      setMapError(MAP_STYLE_CONFIG_ERROR);
      return undefined;
    }
    setMapError(null);
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      bounds: nav.bbox ? [[nav.bbox[0], nav.bbox[1]], [nav.bbox[2], nav.bbox[3]]] : undefined,
      fitBoundsOptions: { padding: 24 },
    });
    mapRef.current = map;
    map.on("error", () => {
      // MapLibre emits this for a failed style/tile load (tile provider outage,
      // network failure, or a response that isn't valid style JSON) — never log the
      // raw event, it may echo the full request URL.
      console.error("MapLibre style load error");
      setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE);
    });
    map.on("load", () => {
      map.addSource("route", { type: "geojson", data: nav.geojson });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        paint: { "line-color": "#e8630a", "line-width": 4 },
      });
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [showPreview, nav]);

  return (
    <div className="pt-2 border-t border-svj-border/60">
      <input ref={inputRef} type="file" accept=".gpx,application/gpx+xml" onChange={onFile} className="hidden" data-testid={`a4d-route-gpx-input-${route.id}`} />

      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">Navigatieroute</div>

      {nav && (
        <div className="mb-2 flex items-center gap-1.5 text-xs text-a4d-green" data-testid={`a4d-route-nav-summary-${route.id}`}>
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span className="font-semibold">
            {sourceType === "builder" ? "Route gemaakt op kaart" : "GPX-route aanwezig"}
          </span>
          <span className="text-slate-400">
            · {(nav.distance_meters / 1000).toFixed(2)} km
            {sourceType === "gpx" && nav.source_filename ? ` · ${nav.source_filename}` : ""}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!nav && (
          <>
            <button
              type="button"
              onClick={openBuilder}
              data-testid={`a4d-route-build-${route.id}`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1.5"
            >
              <MapIcon className="h-3.5 w-3.5" /> Route maken op kaart
            </button>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`a4d-route-gpx-upload-${route.id}`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1.5"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5" />}
              GPX uploaden
            </button>
          </>
        )}

        {nav && (
          <>
            <button
              type="button"
              onClick={togglePreview}
              disabled={loadingPreview}
              data-testid={`a4d-route-gpx-preview-${route.id}`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1"
            >
              {loadingPreview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : showPreview ? "Verberg kaart" : "Preview"}
            </button>

            {sourceType === "builder" ? (
              <button
                type="button"
                onClick={openBuilder}
                data-testid={`a4d-route-build-edit-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1.5"
              >
                <MapIcon className="h-3.5 w-3.5" /> Bewerken op kaart
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={openBuilder}
                  data-testid={`a4d-route-build-rebuild-${route.id}`}
                  className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1.5"
                >
                  <MapIcon className="h-3.5 w-3.5" /> Route opnieuw maken op kaart
                </button>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  data-testid={`a4d-route-gpx-upload-${route.id}`}
                  className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1.5"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} GPX vervangen
                </button>
              </>
            )}
            <button
              type="button"
              onClick={removeNav}
              data-testid={`a4d-route-gpx-remove-${route.id}`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1"
            >
              Verwijderen
            </button>
          </>
        )}
      </div>
      {showPreview && nav?.geojson && (
        <div className="mt-2 h-56 w-full rounded-lg border border-svj-border overflow-hidden relative">
          {/* MapLibre applies its own `.maplibregl-map { position: relative }` CSS
              class to whatever container it's given, colliding with a `position:
              absolute` utility on that same element and collapsing it to height 0
              (fully blank map despite every network request succeeding) — give it a
              plain inner div instead, see RouteBuilderModal.jsx for the full story. */}
          <div className="absolute inset-0">
            <div ref={mapContainerRef} className="w-full h-full" data-testid={`a4d-route-gpx-map-${route.id}`} />
          </div>
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-svj-bg p-3" data-testid={`a4d-route-gpx-map-error-${route.id}`}>
              <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 max-w-sm">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{mapError}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {showBuilder && (
        <RouteBuilderModal
          route={route}
          authAxios={authAxios}
          existingNav={nav}
          eventLocation={eventLocation}
          onClose={() => setShowBuilder(false)}
          onSaved={(data) => {
            setNav(data);
            setShowBuilder(false);
            setShowPreview(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

// Sprint 09D — route image/PDF file upload. event_routes.map_image_url/map_pdf_url
// already existed (previously admin-entered raw URLs only) — this replaces that with
// a real upload flow, reusing the exact multipart-upload convention already proven by
// RouteGpxField/NewsImageField (FormData -> authAxios.post -> toast + onChanged()).
// The upload endpoints update event_routes.map_image_url/map_pdf_url themselves (no
// second PATCH needed), so onImageChanged/onPdfChanged report the new URL back up to
// RouteRow's own form state to keep it in sync until the next full reload — otherwise
// clicking "Opslaan" afterward would silently overwrite the just-uploaded URL with a
// stale value. Entirely independent of GPS navigation (RouteGpxField) — a route may
// have any combination of image/PDF/navigation.
function RouteMediaField({ route, authAxios, onChanged, onImageChanged, onPdfChanged }) {
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [imageUrl, setImageUrl] = useState(route.map_image_url || "");
  const [pdfUrl, setPdfUrl] = useState(route.map_pdf_url || "");

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Bestand te groot (max 8MB)");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/event-routes/${route.id}/upload-image`, fd);
      setImageUrl(data.map_image_url);
      onImageChanged(data.map_image_url);
      toast.success("Afbeelding geüpload");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const removeImage = async () => {
    if (!window.confirm("Route-afbeelding verwijderen?")) return;
    try {
      await authAxios.delete(`/admin/event-routes/${route.id}/image`);
      setImageUrl("");
      onImageChanged("");
      toast.success("Afbeelding verwijderd");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const onPdfFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Bestand te groot (max 15MB)");
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/event-routes/${route.id}/upload-pdf`, fd);
      setPdfUrl(data.map_pdf_url);
      onPdfChanged(data.map_pdf_url);
      toast.success("PDF geüpload");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const removePdf = async () => {
    if (!window.confirm("Route-PDF verwijderen?")) return;
    try {
      await authAxios.delete(`/admin/event-routes/${route.id}/pdf`);
      setPdfUrl("");
      onPdfChanged("");
      toast.success("PDF verwijderd");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-svj-border/60">
      <div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onImageFile}
          className="hidden"
          data-testid={`a4d-route-image-input-${route.id}`}
        />
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">Route-afbeelding</div>
        {imageUrl ? (
          <div className="space-y-1.5">
            <img
              src={imageUrl}
              alt=""
              className="h-20 w-full object-cover rounded-lg border border-svj-border"
              data-testid={`a4d-route-image-thumb-${route.id}`}
            />
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadingImage}
                data-testid={`a4d-route-image-replace-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2 py-1 inline-flex items-center gap-1"
              >
                {uploadingImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />} Afbeelding vervangen
              </button>
              <button
                type="button"
                onClick={removeImage}
                data-testid={`a4d-route-image-remove-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2 py-1"
              >
                Verwijderen
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
            data-testid={`a4d-route-image-upload-${route.id}`}
            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1.5"
          >
            {uploadingImage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Afbeelding uploaden
          </button>
        )}
        <div className="mt-1 text-[10px] text-slate-500">JPG/PNG/WEBP · max 8MB</div>
      </div>

      <div>
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={onPdfFile}
          className="hidden"
          data-testid={`a4d-route-pdf-input-${route.id}`}
        />
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">Route-PDF</div>
        {pdfUrl ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs text-a4d-green" data-testid={`a4d-route-pdf-status-${route.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> <span className="font-semibold">PDF aanwezig</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`a4d-route-pdf-view-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2 py-1"
              >
                PDF bekijken
              </a>
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                disabled={uploadingPdf}
                data-testid={`a4d-route-pdf-replace-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2 py-1 inline-flex items-center gap-1"
              >
                {uploadingPdf ? <Loader2 className="h-3 w-3 animate-spin" /> : null} PDF vervangen
              </button>
              <button
                type="button"
                onClick={removePdf}
                data-testid={`a4d-route-pdf-remove-${route.id}`}
                className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2 py-1"
              >
                Verwijderen
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={uploadingPdf}
            data-testid={`a4d-route-pdf-upload-${route.id}`}
            className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1.5"
          >
            {uploadingPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} PDF uploaden
          </button>
        )}
        <div className="mt-1 text-[10px] text-slate-500">PDF · max 15MB</div>
      </div>
    </div>
  );
}

function RouteRow({ route, dayId, authAxios, onChanged, routeGroups, eventLocation }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAdvancedMedia, setShowAdvancedMedia] = useState(false);
  const [form, setForm] = useState({
    title: route.title || "",
    distance_km: route.distance_km ?? "",
    description: route.description || "",
    map_image_url: route.map_image_url || "",
    map_pdf_url: route.map_pdf_url || "",
    sort_order: route.sort_order ?? 0,
    route_group_id: route.route_group_id || "",
  });
  const groupName = routeGroups.find((g) => g.id === route.route_group_id)?.name;

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titel is verplicht");
    setSaving(true);
    try {
      await authAxios.patch(`/admin/event-routes/${route.id}`, {
        title: form.title.trim(),
        distance_km: form.distance_km === "" ? null : Number(form.distance_km),
        description: form.description || null,
        map_image_url: form.map_image_url || null,
        map_pdf_url: form.map_pdf_url || null,
        sort_order: Number(form.sort_order) || 0,
        route_group_id: form.route_group_id || null,
      });
      toast.success("Route opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Route "${route.title}" verwijderen?`)) return;
    try {
      await authAxios.delete(`/admin/event-routes/${route.id}`);
      toast.success("Route verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`a4d-route-row-${route.id}`}>
        <div className="min-w-0">
          <span className="text-sm text-white font-semibold">{route.title}</span>
          {route.distance_km != null && <span className="ml-2 text-xs text-svj-cyan font-bold">{route.distance_km} km</span>}
          {groupName && <span className="ml-2 text-[10px] uppercase tracking-widest bg-svj-cyan/15 text-svj-cyan rounded-full px-2 py-0.5">{groupName}</span>}
          {(Array.isArray(route.event_route_navigation) ? route.event_route_navigation[0] : route.event_route_navigation) && (
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 rounded-full px-2 py-0.5">GPX</span>
          )}
          {route.map_image_url && (
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-svj-cyan/15 text-svj-cyan rounded-full px-2 py-0.5">Afbeelding</span>
          )}
          {route.map_pdf_url && (
            <span className="ml-2 text-[10px] uppercase tracking-widest bg-svj-cyan/15 text-svj-cyan rounded-full px-2 py-0.5">PDF</span>
          )}
          {route.description && <div className="text-xs text-slate-400 truncate">{route.description}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`a4d-route-edit-${route.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`a4d-route-delete-${route.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`a4d-route-editing-${route.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel (bv. 10 km)" className="sm:col-span-2 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} placeholder="Afstand (km)" type="number" step="0.1" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} placeholder="Volgorde" type="number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </div>
      <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Omschrijving" className="w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Routekeuze (optioneel)</span>
        <div className="mt-1">
          <RouteGroupSelect value={form.route_group_id} onChange={(e) => setForm({ ...form, route_group_id: e.target.value })} routeGroups={routeGroups} testId={`a4d-route-group-${route.id}`} />
        </div>
      </label>
      <RouteMediaField
        route={route}
        authAxios={authAxios}
        onChanged={onChanged}
        onImageChanged={(url) => setForm((f) => ({ ...f, map_image_url: url }))}
        onPdfChanged={(url) => setForm((f) => ({ ...f, map_pdf_url: url }))}
      />
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowAdvancedMedia((v) => !v)}
          data-testid={`a4d-route-media-advanced-toggle-${route.id}`}
          className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-300"
        >
          {showAdvancedMedia ? "Verberg handmatige URL's" : "Geavanceerd: URL handmatig instellen"}
        </button>
        {showAdvancedMedia && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
            <input value={form.map_image_url} onChange={(e) => setForm({ ...form, map_image_url: e.target.value })} placeholder="Kaart-afbeelding URL" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.map_pdf_url} onChange={(e) => setForm({ ...form, map_pdf_url: e.target.value })} placeholder="Kaart-PDF URL" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
        )}
      </div>
      <RouteGpxField route={route} authAxios={authAxios} onChanged={onChanged} eventLocation={eventLocation} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Opslaan
        </button>
      </div>
    </div>
  );
}

function DayCard({ day, authAxios, onChanged, onDeleted, routeGroups, eventLocation }) {
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [addingRoute, setAddingRoute] = useState(false);
  const [routeForm, setRouteForm] = useState(emptyRouteForm());
  const [creatingRoute, setCreatingRoute] = useState(false);
  const [editingDay, setEditingDay] = useState(false);
  const [dayForm, setDayForm] = useState({ day_number: day.day_number, date: day.date || "", label: day.label || "" });
  const [savingDay, setSavingDay] = useState(false);

  const loadRoutes = async () => {
    setLoadingRoutes(true);
    try {
      const { data } = await authAxios.get(`/admin/event-days/${day.id}/routes`);
      setRoutes(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingRoutes(false);
    }
  };

  useEffect(() => {
    loadRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.id]);

  const saveDay = async () => {
    setSavingDay(true);
    try {
      await authAxios.patch(`/admin/event-days/${day.id}`, {
        day_number: Number(dayForm.day_number) || day.day_number,
        date: dayForm.date || null,
        label: dayForm.label || null,
      });
      toast.success("Dag opgeslagen");
      setEditingDay(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingDay(false);
    }
  };

  const deleteDay = async () => {
    if (!window.confirm(`Dag ${day.day_number} verwijderen? Routes en check-ins van deze dag verdwijnen mee.`)) return;
    try {
      await authAxios.delete(`/admin/event-days/${day.id}`);
      toast.success("Dag verwijderd");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const createRoute = async () => {
    if (!routeForm.title.trim()) return toast.error("Titel is verplicht");
    setCreatingRoute(true);
    try {
      await authAxios.post(`/admin/event-days/${day.id}/routes`, {
        title: routeForm.title.trim(),
        distance_km: routeForm.distance_km === "" ? null : Number(routeForm.distance_km),
        description: routeForm.description || null,
        map_image_url: routeForm.map_image_url || null,
        map_pdf_url: routeForm.map_pdf_url || null,
        sort_order: Number(routeForm.sort_order) || 0,
        route_group_id: routeForm.route_group_id || null,
      });
      toast.success("Route toegevoegd");
      setRouteForm(emptyRouteForm());
      setAddingRoute(false);
      loadRoutes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreatingRoute(false);
    }
  };

  return (
    <div className="rounded-xl border border-svj-border bg-svj-bg/40 p-4" data-testid={`a4d-day-${day.id}`}>
      {!editingDay ? (
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-white">Dag {day.day_number}{day.label ? ` — ${day.label}` : ""}</div>
            {day.date && <div className="text-xs text-slate-400">{new Date(day.date).toLocaleDateString("nl-NL", { weekday: "long", day: "2-digit", month: "long" })}</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => setEditingDay(true)} data-testid={`a4d-day-edit-${day.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
              Bewerken
            </button>
            <button type="button" onClick={deleteDay} data-testid={`a4d-day-delete-${day.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={dayForm.day_number} onChange={(e) => setDayForm({ ...dayForm, day_number: e.target.value })} type="number" placeholder="Dagnummer" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={dayForm.date} onChange={(e) => setDayForm({ ...dayForm, date: e.target.value })} type="date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={dayForm.label} onChange={(e) => setDayForm({ ...dayForm, label: e.target.value })} placeholder="Label (bv. Vrijdag)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setEditingDay(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={saveDay} disabled={savingDay} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {savingDay ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {loadingRoutes && <div className="text-xs text-slate-500">Routes laden…</div>}
        {!loadingRoutes && routes.length === 0 && !addingRoute && (
          <div className="text-xs text-slate-500">Nog geen routes voor deze dag.</div>
        )}
        {routes.map((r) => (
          <RouteRow key={r.id} route={r} dayId={day.id} authAxios={authAxios} onChanged={loadRoutes} routeGroups={routeGroups} eventLocation={eventLocation} />
        ))}
        {!addingRoute ? (
          <button type="button" onClick={() => setAddingRoute(true)} data-testid={`a4d-route-add-${day.id}`} className="text-xs font-semibold text-svj-cyan hover:text-white inline-flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Route toevoegen
          </button>
        ) : (
          <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input value={routeForm.title} onChange={(e) => setRouteForm({ ...routeForm, title: e.target.value })} placeholder="Titel (bv. 10 km)" className="sm:col-span-2 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
              <input value={routeForm.distance_km} onChange={(e) => setRouteForm({ ...routeForm, distance_km: e.target.value })} placeholder="Afstand (km)" type="number" step="0.1" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
              <input value={routeForm.sort_order} onChange={(e) => setRouteForm({ ...routeForm, sort_order: e.target.value })} placeholder="Volgorde" type="number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            </div>
            <input value={routeForm.description} onChange={(e) => setRouteForm({ ...routeForm, description: e.target.value })} placeholder="Omschrijving" className="w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <RouteGroupSelect value={routeForm.route_group_id} onChange={(e) => setRouteForm({ ...routeForm, route_group_id: e.target.value })} routeGroups={routeGroups} testId={`a4d-route-group-new-${day.id}`} />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setAddingRoute(false); setRouteForm(emptyRouteForm()); }} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
              <button type="button" onClick={createRoute} disabled={creatingRoute} data-testid={`a4d-route-create-${day.id}`} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
                {creatingRoute ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Route groups (Sprint 01) — event-level distance choice spanning multiple days --

function RouteGroupRow({ group, authAxios, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: group.name || "", distance_km: group.distance_km ?? "", sort_order: group.sort_order ?? 0, active: group.active !== false });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setSaving(true);
    try {
      await authAxios.patch(`/admin/route-groups/${group.id}`, {
        name: form.name.trim(),
        distance_km: form.distance_km === "" ? null : Number(form.distance_km),
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
      });
      toast.success("Routekeuze opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Routekeuze "${group.name}" verwijderen? Routes die eraan gekoppeld zijn blijven bestaan, maar verliezen de koppeling.`)) return;
    try {
      await authAxios.delete(`/admin/route-groups/${group.id}`);
      toast.success("Routekeuze verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`a4d-route-group-row-${group.id}`}>
        <div className="min-w-0 flex items-center gap-2">
          <span className="text-sm text-white font-semibold">{group.name}</span>
          {group.distance_km != null && <span className="text-xs text-svj-cyan font-bold">{group.distance_km} km</span>}
          {group.active === false && <span className="text-[10px] uppercase tracking-widest bg-svj-border text-slate-400 rounded-full px-2 py-0.5">Inactief</span>}
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} data-testid={`a4d-route-group-edit-${group.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">
            Bewerken
          </button>
          <button type="button" onClick={del} data-testid={`a4d-route-group-delete-${group.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2" data-testid={`a4d-route-group-editing-${group.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Naam (bv. 5 km)" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} placeholder="Afstand (km)" type="number" step="0.1" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
        <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} placeholder="Volgorde" type="number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-svj-border bg-svj-bg" />
        <span className="text-xs text-slate-300">Actief (selecteerbaar)</span>
      </label>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
          <X className="h-3.5 w-3.5" /> Annuleren
        </button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function RouteGroupsManager({ eventId, authAxios, routeGroups, loading, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", distance_km: "", sort_order: 0 });

  const create = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/route-groups`, {
        name: form.name.trim(),
        distance_km: form.distance_km === "" ? null : Number(form.distance_km),
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success("Routekeuze toegevoegd");
      setForm({ name: "", distance_km: "", sort_order: 0 });
      setAdding(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <RouteIcon className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Routekeuzes</div>
          <span className="text-xs text-slate-400">({routeGroups.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="a4d-route-group-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Routekeuze
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Bijvoorbeeld "5 km" en "10 km" — koppel hieronder bij "Dagen &amp; routes" per dagroute welke keuze erbij hoort.
        Een routekeuze mag over meerdere dagen dezelfde afstand aanduiden.
      </p>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && routeGroups.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen routekeuzes voor dit event.</div>
      )}

      {adding && (
        <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Naam (bv. 5 km)" data-testid="a4d-route-group-name" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} placeholder="Afstand (km)" type="number" step="0.1" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} placeholder="Volgorde" type="number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="a4d-route-group-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {routeGroups.map((g) => (
          <RouteGroupRow key={g.id} group={g} authAxios={authAxios} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function DaysManager({ eventId, authAxios, routeGroups, eventLocation }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ day_number: "", date: "", label: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/days`);
      setDays(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const create = async () => {
    const dayNumber = Number(form.day_number) || days.length + 1;
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/days`, {
        day_number: dayNumber,
        date: form.date || null,
        label: form.label || null,
      });
      toast.success("Dag toegevoegd");
      setForm({ day_number: "", date: "", label: "" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Dagen &amp; routes</div>
          <span className="text-xs text-slate-400">({days.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="a4d-day-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Dag
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && days.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen dagen ingesteld voor dit meerdaagse event.</div>
      )}

      {adding && (
        <div className="mb-4 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input value={form.day_number} onChange={(e) => setForm({ ...form, day_number: e.target.value })} type="number" placeholder={`Dagnummer (bv. ${days.length + 1})`} data-testid="a4d-day-number" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} type="date" data-testid="a4d-day-date" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (bv. Vrijdag)" data-testid="a4d-day-label" className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="a4d-day-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Toevoegen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {days.map((d) => (
          <DayCard key={d.id} day={d} authAxios={authAxios} onChanged={load} onDeleted={load} routeGroups={routeGroups} eventLocation={eventLocation} />
        ))}
      </div>
    </div>
  );
}

// --- Info settings (Sprint 05, Part A) --------------------------------------

function InfoSettingsManager({ eventId, authAxios, location }) {
  const [form, setForm] = useState({
    info_text: "", contact_name: "", contact_email: "", contact_phone: "", latitude: "", longitude: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/companion-settings`);
      setForm({
        info_text: data.info_text || "",
        contact_name: data.contact_name || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        latitude: data.latitude ?? "",
        longitude: data.longitude ?? "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.put(`/admin/events/${eventId}/companion-settings`, {
        info_text: form.info_text || null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
      });
      toast.success("Informatie opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Reuses the exact same public Open-Meteo geocoding call the Companion App itself
  // falls back to (companion-app/src/lib/weather.js) — a one-time admin-side lookup so
  // coordinates only need to be resolved once per event, not once per participant.
  const geocode = async () => {
    if (!location?.trim()) {
      toast.error("Vul eerst een locatie in bij de eventgegevens");
      return;
    }
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?count=1&language=nl&name=${encodeURIComponent(location.trim())}`,
      );
      const geo = await res.json();
      const place = geo?.results?.[0];
      if (!place) {
        toast.error("Locatie niet gevonden");
        return;
      }
      setForm((f) => ({ ...f, latitude: place.latitude, longitude: place.longitude }));
      toast.success(`Coördinaten gevonden voor ${place.name}`);
    } catch {
      toast.error("Kon coördinaten niet ophalen");
    } finally {
      setGeocoding(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Laden…</div>;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Info className="h-4 w-4 text-svj-cyan" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Informatie</div>
      </div>
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Informatietekst voor deelnemers</span>
          <textarea
            value={form.info_text}
            onChange={(e) => setForm({ ...form, info_text: e.target.value })}
            placeholder="Praktische informatie over het evenement…"
            rows={3}
            data-testid="a4d-info-text"
            className="mt-1 w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan resize-y"
          />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Contactpersoon</span>
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Naam (optioneel — anders organisatie)" data-testid="a4d-info-contact-name" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Contact e-mail</span>
            <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="optioneel — anders organisatie" data-testid="a4d-info-contact-email" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Contact telefoon</span>
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="optioneel — anders organisatie" data-testid="a4d-info-contact-phone" className="mt-1 w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          </label>
        </div>
        <div>
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Coördinaten (voor het weerbericht)</span>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="Breedtegraad" type="number" step="any" data-testid="a4d-info-lat" className="w-36 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="Lengtegraad" type="number" step="any" data-testid="a4d-info-lng" className="w-36 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
            <button type="button" onClick={geocode} disabled={geocoding} data-testid="a4d-info-geocode" className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1.5 inline-flex items-center gap-1.5">
              {geocoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Compass className="h-3.5 w-3.5" />} Ophalen op basis van locatie
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Eén keer instellen voorkomt dat elke deelnemer zijn telefoon apart laat geocoderen.</p>
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={save} disabled={saving} data-testid="a4d-info-save" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}

// --- News ------------------------------------------------------------------

// Sprint 04: shared file-input + preview + remove control for an event message's
// optional image, reused by both the edit form (NewsRow) and the "add message" form
// (NewsManager) — same upload convention as the existing event cover-image field
// (admin_upload_event_cover / AdminEventForm.jsx), just posted to the news-specific
// upload endpoint instead.
function NewsImageField({ eventId, authAxios, value, onChange, testIdPrefix }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authAxios.post(`/admin/events/${eventId}/news/upload-image`, fd);
      onChange(data.image_url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-image-input`}
      />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="" className="h-16 w-16 rounded-lg object-cover border border-svj-border" />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testIdPrefix}-image-replace`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1 inline-flex items-center gap-1"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Vervangen
            </button>
            <button
              type="button"
              onClick={() => onChange(null)}
              data-testid={`${testIdPrefix}-image-remove`}
              className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1"
            >
              Verwijderen
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-image-upload`}
          className="inline-flex items-center gap-2 rounded-lg border border-dashed border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Afbeelding toevoegen
        </button>
      )}
    </div>
  );
}

// Sprint 09F — important event/route alerts, layered onto the same event_news
// admin management surface (never a second, parallel "alerts" admin page). Shared by
// both the "add message" form and the edit form below, exactly like NewsImageField
// already is — no duplicated alert-scope UI.
const ALERT_SEVERITIES = [
  { value: "info", label: "Informatie" },
  { value: "warning", label: "Waarschuwing" },
  { value: "critical", label: "Kritiek" },
];

function AlertTypeFields({ authAxios, days, form, setForm, idPrefix }) {
  // Which day's routes are currently being browsed for the "specific route" scope —
  // purely local UI state, never sent to the backend (only form.route_id is sent).
  const [routeDayId, setRouteDayId] = useState("");
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  useEffect(() => {
    if (!routeDayId) {
      setRoutes([]);
      return;
    }
    setLoadingRoutes(true);
    authAxios
      .get(`/admin/event-days/${routeDayId}/routes`)
      .then(({ data }) => setRoutes(data.items || []))
      .catch(() => setRoutes([]))
      .finally(() => setLoadingRoutes(false));
  }, [routeDayId, authAxios]);

  const scope = form.route_id ? "route" : form.day_id ? "day" : "event";

  return (
    <div className="space-y-2 border-t border-svj-border/60 pt-2">
      <div className="flex items-center gap-4 text-xs font-semibold text-slate-300">
        <label className="inline-flex items-center gap-1.5">
          <input
            type="radio"
            name={`${idPrefix}-type`}
            checked={form.message_type !== "alert"}
            onChange={() => setForm((f) => ({ ...f, message_type: "news", severity: "", day_id: "", route_id: "" }))}
            data-testid={`${idPrefix}-type-news`}
          />
          Nieuws
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input
            type="radio"
            name={`${idPrefix}-type`}
            checked={form.message_type === "alert"}
            onChange={() => setForm((f) => ({ ...f, message_type: "alert", severity: f.severity || "info" }))}
            data-testid={`${idPrefix}-type-alert`}
          />
          Belangrijke melding
        </label>
      </div>

      {form.message_type === "alert" && (
        <div className="space-y-2 pl-1">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Ernst</span>
            <select
              value={form.severity || "info"}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              data-testid={`${idPrefix}-severity`}
              className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
            >
              {ALERT_SEVERITIES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Geldt voor</span>
            <select
              value={scope}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "event") {
                  setForm((f) => ({ ...f, day_id: "", route_id: "" }));
                  setRouteDayId("");
                } else if (v === "day") {
                  setForm((f) => ({ ...f, route_id: "" }));
                  setRouteDayId("");
                } else {
                  setForm((f) => ({ ...f, day_id: "" }));
                }
              }}
              data-testid={`${idPrefix}-scope`}
              className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
            >
              <option value="event">Hele evenement</option>
              <option value="day">Specifieke dag</option>
              <option value="route">Specifieke route</option>
            </select>
          </label>

          {scope === "day" && (
            <select
              value={form.day_id || ""}
              onChange={(e) => setForm((f) => ({ ...f, day_id: e.target.value }))}
              data-testid={`${idPrefix}-day`}
              className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
            >
              <option value="">Kies een dag…</option>
              {days.map((d) => (
                <option key={d.id} value={d.id}>Dag {d.day_number}{d.label ? ` — ${d.label}` : ""}</option>
              ))}
            </select>
          )}

          {scope === "route" && (
            <div className="grid grid-cols-2 gap-2">
              <select
                value={routeDayId}
                onChange={(e) => {
                  setRouteDayId(e.target.value);
                  setForm((f) => ({ ...f, route_id: "" }));
                }}
                data-testid={`${idPrefix}-route-day`}
                className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
              >
                <option value="">Kies een dag…</option>
                {days.map((d) => (
                  <option key={d.id} value={d.id}>Dag {d.day_number}{d.label ? ` — ${d.label}` : ""}</option>
                ))}
              </select>
              <select
                value={form.route_id || ""}
                onChange={(e) => setForm((f) => ({ ...f, route_id: e.target.value }))}
                disabled={!routeDayId || loadingRoutes}
                data-testid={`${idPrefix}-route`}
                className="h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm"
              >
                <option value="">{loadingRoutes ? "Laden…" : "Kies een route…"}</option>
                {routes.map((r) => (
                  <option key={r.id} value={r.id}>{r.title}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Actief vanaf</span>
              <input
                type="datetime-local"
                value={form.active_from || ""}
                onChange={(e) => setForm((f) => ({ ...f, active_from: e.target.value }))}
                data-testid={`${idPrefix}-active-from`}
                className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Actief tot</span>
              <input
                type="datetime-local"
                value={form.active_until || ""}
                onChange={(e) => setForm((f) => ({ ...f, active_until: e.target.value }))}
                data-testid={`${idPrefix}-active-until`}
                className="mt-1 h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm w-full"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

const ALERT_SEVERITY_BADGE = {
  info: "bg-blue-500/15 text-blue-300",
  warning: "bg-amber-500/15 text-amber-300",
  critical: "bg-red-500/15 text-red-300",
};

// datetime-local inputs give a bare local-time string with no timezone; Date parses
// that as the browser's own local time, and toISOString() converts it correctly to
// UTC for the backend — same conversion needed in both the add and edit forms below.
function localDateTimeToIso(value) {
  return value ? new Date(value).toISOString() : null;
}

function NewsRow({ item, eventId, authAxios, onChanged, days }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: item.title || "",
    body: item.body || "",
    image_url: item.image_url || null,
    message_type: item.message_type || "news",
    severity: item.severity || "",
    day_id: item.day_id || "",
    route_id: item.route_id || "",
    active_from: "",
    active_until: "",
  });

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titel is verplicht");
    if (form.message_type === "alert" && !form.severity) return toast.error("Kies een ernst voor deze melding");
    setSaving(true);
    try {
      await authAxios.patch(`/admin/event-news/${item.id}`, {
        title: form.title.trim(),
        body: form.body || null,
        image_url: form.image_url || null,
        message_type: form.message_type,
        severity: form.message_type === "alert" ? form.severity : null,
        day_id: form.message_type === "alert" ? form.day_id || null : null,
        route_id: form.message_type === "alert" ? form.route_id || null : null,
        active_from: form.message_type === "alert" ? localDateTimeToIso(form.active_from) : null,
        active_until: form.message_type === "alert" ? localDateTimeToIso(form.active_until) : null,
      });
      toast.success(form.message_type === "alert" ? "Melding opgeslagen" : "Nieuwsbericht opgeslagen");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Nieuwsbericht "${item.title}" verwijderen?`)) return;
    try {
      await authAxios.delete(`/admin/event-news/${item.id}`);
      toast.success("Verwijderd");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  if (!editing) {
    return (
      <div className="px-3 py-2 rounded-lg bg-svj-bg/60 border border-svj-border" data-testid={`a4d-news-row-${item.id}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-semibold text-white">{item.title}</div>
              {item.message_type === "alert" && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${ALERT_SEVERITY_BADGE[item.severity] || ALERT_SEVERITY_BADGE.info}`}
                  data-testid={`a4d-news-alert-badge-${item.id}`}
                >
                  <AlertTriangle className="h-3 w-3" /> Melding · {ALERT_SEVERITIES.find((s) => s.value === item.severity)?.label || item.severity}
                </span>
              )}
            </div>
            {item.published_at && <div className="text-[10px] text-slate-500">{new Date(item.published_at).toLocaleString("nl-NL")}</div>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button type="button" onClick={() => setEditing(true)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-2.5 py-1">Bewerken</button>
            <button type="button" onClick={del} data-testid={`a4d-news-delete-${item.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 text-xs font-semibold px-2.5 py-1">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {item.image_url && (
          <img src={item.image_url} alt="" className="mt-2 h-24 w-full max-w-xs rounded-lg object-cover border border-svj-border" />
        )}
        {item.body && <p className="mt-1 text-xs text-slate-400 whitespace-pre-line">{item.body}</p>}
      </div>
    );
  }

  return (
    <div className="px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel" className="w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
      <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Bericht" rows={3} className="w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan resize-y" />
      <NewsImageField
        eventId={eventId}
        authAxios={authAxios}
        value={form.image_url}
        onChange={(url) => setForm({ ...form, image_url: url })}
        testIdPrefix={`a4d-news-edit-${item.id}`}
      />
      <AlertTypeFields authAxios={authAxios} days={days} form={form} setForm={setForm} idPrefix={`a4d-news-edit-${item.id}`} />
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setEditing(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
        <button type="button" onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Opslaan
        </button>
      </div>
    </div>
  );
}

function NewsManager({ eventId, authAxios }) {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "",
    body: "",
    image_url: null,
    message_type: "news",
    severity: "",
    day_id: "",
    route_id: "",
    active_from: "",
    active_until: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/news`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    authAxios.get(`/admin/events/${eventId}/days`).then(({ data }) => setDays(data.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const emptyForm = () => ({
    title: "", body: "", image_url: null, message_type: "news", severity: "",
    day_id: "", route_id: "", active_from: "", active_until: "",
  });

  const create = async () => {
    if (!form.title.trim()) return toast.error("Titel is verplicht");
    if (form.message_type === "alert" && !form.severity) return toast.error("Kies een ernst voor deze melding");
    setCreating(true);
    try {
      await authAxios.post(`/admin/events/${eventId}/news`, {
        title: form.title.trim(),
        body: form.body || null,
        image_url: form.image_url || null,
        message_type: form.message_type,
        severity: form.message_type === "alert" ? form.severity : null,
        day_id: form.message_type === "alert" ? form.day_id || null : null,
        route_id: form.message_type === "alert" ? form.route_id || null : null,
        active_from: form.message_type === "alert" ? localDateTimeToIso(form.active_from) : null,
        active_until: form.message_type === "alert" ? localDateTimeToIso(form.active_until) : null,
      });
      toast.success(form.message_type === "alert" ? "Melding geplaatst" : "Nieuwsbericht geplaatst");
      setForm(emptyForm());
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-svj-cyan" />
          <div className="text-sm font-semibold text-white uppercase tracking-wider">Nieuwsberichten</div>
          <span className="text-xs text-slate-400">({items.length})</span>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} data-testid="a4d-news-add" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Bericht
          </button>
        )}
      </div>

      {loading && <div className="text-sm text-slate-400">Laden…</div>}
      {!loading && items.length === 0 && !adding && (
        <div className="text-sm text-slate-400">Nog geen nieuwsberichten.</div>
      )}

      {adding && (
        <div className="mb-3 px-3 py-3 rounded-lg bg-svj-bg/60 border border-svj-border space-y-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel" data-testid="a4d-news-title" className="w-full h-9 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Bericht" rows={3} data-testid="a4d-news-body" className="w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan resize-y" />
          <NewsImageField
            eventId={eventId}
            authAxios={authAxios}
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            testIdPrefix="a4d-news-add"
          />
          <AlertTypeFields authAxios={authAxios} days={days} form={form} setForm={setForm} idPrefix="a4d-news-add" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setAdding(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="a4d-news-create" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-3 py-1.5 inline-flex items-center gap-1">
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Plaatsen
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.map((n) => (
          <NewsRow key={n.id} item={n} eventId={eventId} authAxios={authAxios} onChanged={load} days={days} />
        ))}
      </div>
    </div>
  );
}

// --- Main -------------------------------------------------------------------

// Sprint 09H-B §3 — a small, honest status panel next to the incident_reporting_
// enabled toggle (which lives in AdminEventForm.jsx, same pattern as
// companion_app_enabled). Shows the real number of active organizer push receivers
// so an organizer can never be misled into thinking incident push is operational
// when nobody has actually opted in on a device yet.
// Safe, Dutch labels for webpush_service.validate_vapid_config()'s error codes —
// never a raw exception message, never key material.
const VAPID_ERROR_LABELS = {
  vapid_missing: "VAPID-sleutels zijn niet geconfigureerd op de server.",
  vapid_invalid_private_key: "De VAPID private key heeft een ongeldig formaat (controleer op per ongeluk meegekopieerde aanhalingstekens of witruimte).",
  vapid_invalid_public_key: "De VAPID public key heeft een ongeldig formaat.",
  vapid_key_mismatch: "De VAPID public en private key vormen geen bij elkaar horend sleutelpaar.",
  vapid_invalid_subject: "Het VAPID subject moet een geldig mailto:- of https:-adres zijn.",
};

// Native Android/iOS participant push (StapKompas wrapper apps) uses a completely
// separate server credential (FCM_SERVICE_ACCOUNT_JSON op Railway) from VAPID —
// a real device test found a participant's "Pushmeldingen: Aan" toggle showing
// active with no notification ever arriving, traced to this credential not being
// visible anywhere in the admin panel at all (only per-incident attempt logs).
const FCM_ERROR_LABELS = {
  fcm_missing: "FCM_SERVICE_ACCOUNT_JSON is niet geconfigureerd op de server — nodig voor pushmeldingen naar de native Android/iOS StapKompas-app.",
  fcm_invalid_service_account_json: "FCM_SERVICE_ACCOUNT_JSON bevat geen geldige JSON.",
  fcm_invalid_service_account: "FCM_SERVICE_ACCOUNT_JSON mist verplichte velden (project_id/client_email/private_key) of kon niet worden geladen.",
};

function IncidentSettingsPanel({ authAxios, incidentReportingEnabled }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadStatus = () => {
    authAxios.get("/admin/push/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  };

  useEffect(() => {
    if (!incidentReportingEnabled) return;
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, incidentReportingEnabled]);

  if (!incidentReportingEnabled) return null;

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await authAxios.post("/admin/push/test");
      setTestResult(data);
      if (data.accepted > 0) {
        toast.success("Pushservice heeft testmelding geaccepteerd.");
      } else if (data.attempted === 0) {
        toast.error("Geen actief pushabonnement op dit apparaat om te testen.");
      } else {
        toast.error("Push verzenden mislukt.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
      loadStatus();
    }
  };

  const vapidOk = status?.vapid_valid;
  const pushServiceLabel = status == null ? "…" : vapidOk ? "Gereed" : "Configuratiefout";

  return (
    <div className="rounded-2xl border border-red-500/30 overflow-hidden bg-svj-card p-5">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-400" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Incidentmeldingen</div>
      </div>
      <div className="mt-3 text-sm text-slate-300">
        Incidentfunctie: <span className="font-bold text-emerald-400">Actief</span>
      </div>
      <div className="mt-1 text-sm text-slate-300">
        Pushservice (browser): <span className={`font-bold ${vapidOk ? "text-emerald-400" : status ? "text-red-400" : "text-slate-400"}`}>{pushServiceLabel}</span>
      </div>
      <div className="mt-1 text-sm text-slate-300">
        Pushservice (native app):{" "}
        <span
          className={`font-bold ${status ? (status.fcm_valid ? "text-emerald-400" : "text-red-400") : "text-slate-400"}`}
        >
          {status == null ? "…" : status.fcm_valid ? "Gereed" : "Configuratiefout"}
        </span>
      </div>
      <div className="mt-1 text-sm text-slate-300">
        Pushontvangers: <span className="font-bold text-white">{status ? status.active_subscriptions : "…"} apparaat{status?.active_subscriptions === 1 ? "" : "en"}</span>
      </div>
      {status?.fcm_project_id && (
        <div className="mt-1 text-sm text-slate-300">
          FCM-project (backend): <span className="font-bold text-white">{status.fcm_project_id}</span>
          <span className="block text-xs text-slate-400 mt-0.5">
            Moet exact overeenkomen met het project-ID in GoogleService-Info.plist / google-services.json ("stapkompas") — een afwijking hier betekent dat de backend-credential (FCM_SERVICE_ACCOUNT_JSON op Railway) bij een ander Firebase-project hoort dan de apps zelf.
          </span>
        </div>
      )}

      {status && status.vapid_error_code && (
        <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {VAPID_ERROR_LABELS[status.vapid_error_code] || "Pushconfiguratie ongeldig."}
        </div>
      )}
      {status && status.fcm_error_code && (
        <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {FCM_ERROR_LABELS[status.fcm_error_code] || "FCM-configuratie ongeldig."}
        </div>
      )}
      {status && vapidOk && status.active_subscriptions === 0 && (
        <div className="mt-3 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          Nog geen organisator heeft pushmeldingen ingeschakeld op dit apparaat. Meldingen worden wel opgeslagen, maar er is nu geen directe pushnotificatie. Schakel pushmeldingen in via het menu links.
        </div>
      )}

      <button
        type="button"
        onClick={runTest}
        disabled={testing || !vapidOk}
        data-testid="admin-push-test-button"
        className="mt-4 inline-flex items-center gap-2 rounded-full border border-svj-border text-slate-200 hover:text-white disabled:opacity-40 text-xs font-bold px-4 py-2"
      >
        {testing ? "Bezig…" : "Test pushmelding"}
      </button>
      {testResult && (
        <div className="mt-2 text-xs text-slate-400" data-testid="admin-push-test-result">
          {testResult.attempted === 0
            ? "Geen actief pushabonnement op dit apparaat."
            : `${testResult.accepted}/${testResult.attempted} geaccepteerd door de pushservice.`}
        </div>
      )}
    </div>
  );
}

function HuntStatsPanel({ eventId, authAxios, scavengerHuntEnabled }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!eventId || !scavengerHuntEnabled) return;
      setLoading(true);
      try {
        const { data } = await authAxios.get(`/admin/events/${eventId}/hunt-stats`);
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [eventId, authAxios, scavengerHuntEnabled]);

  if (!scavengerHuntEnabled) return null;

  return (
    <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5" data-testid="admin-hunt-stats">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-teal-400" />
        <div className="text-sm font-semibold text-white">Zoektocht</div>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Zoekpunten plaats je bij Dagen &amp; routes → een route → Punten → type “Zoekpunt”. Deelnemers kiezen zelf
        of ze meedoen.
      </p>

      {loading && <div className="text-xs text-slate-400">Laden…</div>}

      {!loading && stats && (
        <>
          <div className="text-xs text-slate-300 mb-3">
            <span className="text-lg font-extrabold text-white">{stats.participants}</span>{" "}
            {stats.participants === 1 ? "deelnemer doet mee" : "deelnemers doen mee"}
            {stats.ever_joined > stats.participants && (
              <span className="text-slate-500"> · {stats.ever_joined - stats.participants} gestopt</span>
            )}
          </div>

          {(stats.routes || []).length === 0 ? (
            <div className="text-xs text-slate-400">
              Nog geen zoekpunten geplaatst. Zonder zoekpunten ziet een deelnemer geen zoektocht.
            </div>
          ) : (
            <div className="space-y-2">
              {stats.routes.map((r) => (
                <div
                  key={r.route_id}
                  className="rounded-lg border border-svj-border bg-svj-bg px-3 py-2"
                  data-testid={`admin-hunt-route-${r.route_id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-white">
                      {r.day_number ? `Dag ${r.day_number} · ` : ""}
                      {r.route_title || "Route"}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {r.total_points} {r.total_points === 1 ? "punt" : "punten"}
                      {r.with_choice > 0 ? ` · ${r.with_choice} meerkeuze` : ""}
                      {r.with_open > 0 ? ` · ${r.with_open} open vraag` : ""}
                      {r.with_task > 0 ? ` · ${r.with_task} opdracht` : ""}
                      {r.with_hint > 0 ? ` · ${r.with_hint} met hint` : ""}
                    </span>
                  </div>
                  {(r.ordered || r.anagram) && (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {r.ordered && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-300 border border-teal-500/30">
                          Op volgorde
                        </span>
                      )}
                      {r.anagram && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-300 border border-teal-500/30">
                          Woordpuzzel
                        </span>
                      )}
                    </div>
                  )}

                  {/* Prijs: wie was er als eerste met het juiste antwoord (2026-09-12).
                      Alleen zichtbaar hier, voor de organisator — nooit aan een
                      deelnemer getoond, zodat het geen wedstrijdje/race wordt tijdens
                      het lopen. */}
                  {(r.challenge_points || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-svj-border space-y-1" data-testid={`admin-hunt-prizes-${r.route_id}`}>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-300">
                        <Trophy className="h-3 w-3" /> Prijs: eerste met het juiste antwoord
                      </div>
                      {r.challenge_points.map((cp) => (
                        <div
                          key={cp.point_id}
                          className="flex items-center justify-between gap-2 text-[11px]"
                          data-testid={`admin-hunt-prize-${cp.point_id}`}
                        >
                          <span className="text-slate-400 truncate">{cp.title || "Zoekpunt"}</span>
                          {cp.winner ? (
                            <span
                              className="text-amber-200 font-semibold whitespace-nowrap"
                              title={cp.winner.answered_at ? new Date(cp.winner.answered_at).toLocaleString("nl-NL") : undefined}
                            >
                              {cp.winner.holder_name || "Onbekend"}
                            </span>
                          ) : (
                            <span className="text-slate-600 whitespace-nowrap">nog niemand</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs text-slate-400">
                      Codewoord:{" "}
                      <span className="font-mono font-bold tracking-[0.2em] text-teal-300">
                        {r.codeword || "—"}
                      </span>
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {r.participants_completed} afgerond
                    </span>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-slate-500">
                Het codewoord staat hier zodat je bij de finish kunt controleren wat een deelnemer inlevert. Bij
                een woordpuzzel telt een route pas als afgerond zodra de deelnemer het woord ook echt heeft
                ingevuld.
              </p>
            </div>
          )}

          {/* Woordpuzzel voor het hele evenement (Sprint 09K ronde 3) — een aparte,
              organisator-only weergave: per routekeuze (of "geen routekeuze" voor
              tickets zonder groep) het woord dat over alle dagen samen ontstaat. */}
          {stats.event_codeword?.enabled && (
            <div
              className="mt-3 rounded-lg border border-teal-500/30 bg-teal-500/5 px-3 py-2.5"
              data-testid="admin-hunt-event-codeword"
            >
              <div className="flex items-center gap-1.5 text-xs font-bold text-teal-300 mb-2">
                <KeyRound className="h-3.5 w-3.5" /> Woordpuzzel voor het hele evenement
              </div>
              {stats.event_codeword.groups.length === 0 ? (
                <div className="text-[11px] text-slate-500">Nog geen routes met zoekpunten.</div>
              ) : (
                <div className="space-y-2">
                  {stats.event_codeword.groups.map((g) => (
                    <div
                      key={g.route_group_id || "legacy"}
                      className="flex items-center justify-between gap-2 flex-wrap"
                      data-testid={`admin-hunt-event-codeword-group-${g.route_group_id || "legacy"}`}
                    >
                      <span className="text-xs text-slate-300">{g.route_group_name}</span>
                      <span className="text-xs text-slate-400">
                        <span className="font-mono font-bold tracking-[0.2em] text-teal-300">
                          {g.codeword || "—"}
                        </span>{" "}
                        · {g.participants_completed} afgerond
                        {g.winner && (
                          <>
                            {" "}
                            ·{" "}
                            <span
                              className="text-amber-200 font-semibold"
                              title={g.winner.answered_at ? new Date(g.winner.answered_at).toLocaleString("nl-NL") : undefined}
                            >
                              eerst: {g.winner.holder_name || "Onbekend"}
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                Elke routekeuze ziet alleen haar eigen routes over alle dagen samen — een deelnemer zonder
                routekeuze ziet alles. Dit woord komt bovenop, niet in plaats van, de losse routewoorden hierboven.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}


export default function AdminEventCompanion({ eventId, authAxios, enabled, location, incidentReportingEnabled, scavengerHuntEnabled }) {
  // Fetched once here (not inside RouteGroupsManager/DaysManager separately) so both
  // the group-management card and the per-day route-assignment selects always agree —
  // creating/renaming/deactivating a group refreshes both in one place.
  const [routeGroups, setRouteGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const loadRouteGroups = async () => {
    if (!eventId) return;
    setLoadingGroups(true);
    try {
      const { data } = await authAxios.get(`/admin/events/${eventId}/route-groups`);
      setRouteGroups(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (eventId && enabled) loadRouteGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, enabled]);

  if (!eventId || !enabled) return null;

  return (
    <div className="mt-6 space-y-6">
      <div className="flex items-center gap-2">
        <MapPinned className="h-4 w-4 text-svj-cyan" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Companion-app (meerdaags)</div>
      </div>
      <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5">
        <RouteGroupsManager eventId={eventId} authAxios={authAxios} routeGroups={routeGroups} loading={loadingGroups} onChanged={loadRouteGroups} />
      </div>
      <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5">
        <DaysManager eventId={eventId} authAxios={authAxios} routeGroups={routeGroups} eventLocation={location} />
      </div>
      <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5">
        <InfoSettingsManager eventId={eventId} authAxios={authAxios} location={location} />
      </div>
      <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5">
        <NewsManager eventId={eventId} authAxios={authAxios} />
      </div>
      <div className="rounded-2xl border border-svj-border overflow-hidden bg-svj-card p-5">
        <CompanionPresence eventId={eventId} authAxios={authAxios} />
      </div>
      <HuntStatsPanel eventId={eventId} authAxios={authAxios} scavengerHuntEnabled={scavengerHuntEnabled} />
      <IncidentSettingsPanel authAxios={authAxios} incidentReportingEnabled={incidentReportingEnabled} />
    </div>
  );
}
